import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations, languageOptions, localeMap } from "./translations";

const STORAGE_KEY = "fitlog_language";

const I18nContext = createContext({
    language: "en",
    setLanguage: () => {},
    t: (key) => key,
    locale: "en-US",
    languageOptions: [],
    pathPrefix: "",
    localizePath: (path) => path,
});

const normalizeLanguage = (value) => (value === "uk" ? "uk" : "en");
const getPathPrefix = (lang) => (lang === "uk" ? "/uk" : "");

const readTranslation = (lang, key) => {
    if (!key) return undefined;
    return key.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), translations[lang]);
};

const formatTemplate = (template, params = {}) =>
    String(template).replace(/\{(\w+)\}/g, (match, key) => {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            return String(params[key]);
        }
        return match;
    });

const getInitialLanguage = () => {
    if (typeof window === "undefined") {
        return "en";
    }
    return getLanguageFromPath(window.location.pathname);
};

export function I18nProvider({ children }) {
    const [language, setLanguageState] = useState(getInitialLanguage);

    const setLanguage = useCallback((next) => {
        const normalized = normalizeLanguage(next);
        setLanguageState(normalized);
        try {
            window.localStorage.setItem(STORAGE_KEY, normalized);
        } catch (_) {
            // ignore storage errors
        }
    }, []);

    const t = useCallback(
        (key, params) => {
            const value = readTranslation(language, key) ?? readTranslation("en", key) ?? key;
            return formatTemplate(value, params);
        },
        [language]
    );

    const locale = localeMap[language] || "en-US";
    const pathPrefix = getPathPrefix(language);
    const localizePath = useCallback(
        (path) => {
            const raw = String(path || "");
            if (!raw) return pathPrefix || "/";
            const normalized = raw.startsWith("/") ? raw : `/${raw}`;
            if (normalized === "/") {
                return pathPrefix || "/";
            }
            return `${pathPrefix}${normalized}`;
        },
        [pathPrefix]
    );

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.lang = language === "uk" ? "uk" : "en";
        }
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t,
            locale,
            languageOptions,
            pathPrefix,
            localizePath,
        }),
        [language, setLanguage, t, locale, pathPrefix, localizePath]
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}

export function getLanguageFromPath(pathname = "") {
    return String(pathname).startsWith("/uk") ? "uk" : "en";
}
